import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import './ScheduleView.scss';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS_BADGE = {
  pending:   'badge bg-warning text-dark',
  confirmed: 'badge bg-success',
  cancelled: 'badge bg-danger',
};

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(isoDate, n) {
  const d = new Date(isoDate + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmt12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${suffix}`;
}

function formatRefId(id) {
  if (!id) return '';
  return 'DENT-' + id.slice(-6).toUpperCase();
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });
}

export default function ScheduleView() {
  const { authFetch } = useAuth();
  const [selectedDate, setSelectedDate]   = useState(todayISO());
  const [slots, setSlots]                 = useState([]);
  const [appointments, setAppointments]   = useState([]);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');

  // Detail modal
  const [selectedAppt, setSelectedAppt]   = useState(null);
  const [detailNotes, setDetailNotes]     = useState('');
  const [savingNotes, setSavingNotes]     = useState(false);
  const [notesError, setNotesError]       = useState('');
  const [notesSaved, setNotesSaved]       = useState(false);
  const [updatingId, setUpdatingId]       = useState(null);
  const [modalSuccess, setModalSuccess]   = useState('');

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = selectedAppt ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedAppt]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [slotsRes, apptRes] = await Promise.all([
        authFetch(`${API}/api/slots/all?date=${selectedDate}`),
        authFetch(`${API}/api/appointments/?date=${selectedDate}`),
      ]);
      if (!slotsRes.ok) throw new Error('Failed to load slots');
      if (!apptRes.ok)  throw new Error('Failed to load appointments');
      const slotsData = await slotsRes.json();
      const apptData  = await apptRes.json();
      setSlots(slotsData);
      setAppointments(apptData);

      // Auto-select first doctor if current selection has no slots
      const docs = [...new Set(slotsData.map(s => s.doctor_name))].sort();
      setSelectedDoctor(prev => {
        if (prev && docs.includes(prev)) return prev;
        return docs[0] || '';
      });
    } catch (err) {
      setError(err.message === 'Failed to fetch'
        ? 'Unable to connect to the server.'
        : err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, selectedDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Derive unique doctors for the current date
  const doctors = [...new Set(slots.map(s => s.doctor_name))].sort();

  // Build appointment lookup by slot_id
  const apptsBySlotId = {};
  appointments.forEach(a => {
    if (!apptsBySlotId[a.slot_id]) apptsBySlotId[a.slot_id] = [];
    apptsBySlotId[a.slot_id].push(a);
  });

  // Filtered + sorted slots for selected doctor
  const visibleSlots = slots
    .filter(s => s.doctor_name === selectedDoctor)
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  function rowVariant(slotAppts, isActive) {
    if (!isActive) return 'ddv-row--inactive';
    if (!slotAppts || slotAppts.length === 0) return 'ddv-row--empty';
    if (slotAppts.some(a => a.status === 'confirmed')) return 'ddv-row--confirmed';
    if (slotAppts.some(a => a.status === 'pending'))   return 'ddv-row--pending';
    return 'ddv-row--empty';
  }

  // ── Detail modal ─────────────────────────────────────────────────────────────
  function openDetail(appt) {
    setSelectedAppt(appt);
    setDetailNotes(appt.notes || '');
    setNotesError('');
    setNotesSaved(false);
    setModalSuccess('');
  }

  function closeDetail() {
    setSelectedAppt(null);
    setDetailNotes('');
    setNotesError('');
    setNotesSaved(false);
    setModalSuccess('');
  }

  async function updateStatus(apptId, newStatus) {
    setUpdatingId(apptId);
    try {
      const res = await authFetch(`${API}/api/appointments/${apptId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update status');
      }
      setModalSuccess(`Status updated to "${newStatus}".`);
      setTimeout(() => setModalSuccess(''), 3000);
      // Refresh data to re-colour the grid
      await loadData();
      // Keep modal open with updated appointment
      setSelectedAppt(prev => prev ? { ...prev, status: newStatus } : null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  }

  async function saveNotes() {
    if (!selectedAppt) return;
    setSavingNotes(true);
    setNotesError('');
    setNotesSaved(false);
    try {
      const res = await authFetch(`${API}/api/appointments/${selectedAppt._id}/notes`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: detailNotes }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to save notes');
      }
      setSelectedAppt(prev => ({ ...prev, notes: detailNotes }));
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (err) {
      setNotesError(err.message);
    } finally {
      setSavingNotes(false);
    }
  }

  return (
    <div className="ddv-page">
      <div className="ddv-header">
        <h2 className="ddv-title">Doctor Schedule</h2>
        <button className="btn ddv-refresh-btn" onClick={loadData} disabled={loading}>
          {loading ? '↻ Loading…' : '↻ Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Controls */}
      <div className="ddv-controls">
        {/* Date navigation */}
        <div className="ddv-date-nav">
          <button className="btn ddv-nav-btn" onClick={() => setSelectedDate(d => addDays(d, -1))}>
            ← Prev
          </button>
          <button className="btn ddv-today-btn" onClick={() => setSelectedDate(todayISO())}>
            Today
          </button>
          <input
            type="date"
            className="form-control ddv-date-input"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
          />
          <button className="btn ddv-nav-btn" onClick={() => setSelectedDate(d => addDays(d, 1))}>
            Next →
          </button>
        </div>

        {/* Doctor selector */}
        {doctors.length > 0 && (
          <select
            className="form-select ddv-doctor-select"
            value={selectedDoctor}
            onChange={e => setSelectedDoctor(e.target.value)}
          >
            {doctors.map(d => (
              <option key={d} value={d}>Dr. {d}</option>
            ))}
          </select>
        )}
      </div>

      {/* Date label */}
      <div className="ddv-date-label">{formatDate(selectedDate)}</div>

      {/* Grid */}
      {loading ? (
        <div className="ddv-loading">
          <div className="spinner-border" style={{ color: '#583FBC' }} role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : doctors.length === 0 ? (
        <div className="ddv-empty-state">No slots scheduled for this date.</div>
      ) : (
        <div className="ddv-grid-wrap">
          <table className="ddv-grid">
            <thead>
              <tr>
                <th className="ddv-th ddv-th-time">Time</th>
                <th className="ddv-th">Service</th>
                <th className="ddv-th ddv-th-patients">Patients</th>
                <th className="ddv-th ddv-th-capacity">Capacity</th>
                <th className="ddv-th ddv-th-status">Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleSlots.length === 0 ? (
                <tr>
                  <td colSpan={5} className="ddv-no-slots">
                    No slots for Dr. {selectedDoctor} on this date.
                  </td>
                </tr>
              ) : (
                visibleSlots.map(slot => {
                  const slotAppts = apptsBySlotId[slot._id] || [];
                  const activeAppts = slotAppts.filter(a => a.status !== 'cancelled');
                  const variant = rowVariant(activeAppts, slot.is_active);
                  const isClickable = activeAppts.length > 0;

                  return (
                    <tr
                      key={slot._id}
                      className={`ddv-row ${variant} ${isClickable ? 'ddv-row--clickable' : ''}`}
                      title={isClickable ? 'Click to view appointment details' : undefined}
                    >
                      {/* Time */}
                      <td className="ddv-td ddv-td-time">
                        <span className="ddv-time">{fmt12(slot.start_time)}</span>
                        <span className="ddv-time-sep">–</span>
                        <span className="ddv-time">{fmt12(slot.end_time)}</span>
                      </td>

                      {/* Service */}
                      <td className="ddv-td">
                        <span className="ddv-service-badge">{slot.service}</span>
                        <span className="ddv-dept">{slot.department}</span>
                      </td>

                      {/* Patients */}
                      <td className="ddv-td ddv-td-patients">
                        {activeAppts.length === 0 ? (
                          <span className="ddv-no-patients">—</span>
                        ) : (
                          <div className="ddv-patient-list">
                            {activeAppts.map(a => (
                              <button
                                key={a._id}
                                className="ddv-patient-btn"
                                onClick={() => openDetail(a)}
                              >
                                {a.patient_name}
                                <span className={`${STATUS_BADGE[a.status] || 'badge bg-secondary'} ddv-inline-badge`}>
                                  {a.status}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Capacity */}
                      <td className="ddv-td ddv-td-capacity">
                        <span className={`ddv-capacity ${activeAppts.length >= slot.max_patients ? 'ddv-capacity--full' : ''}`}>
                          {activeAppts.length}/{slot.max_patients}
                        </span>
                      </td>

                      {/* Slot status */}
                      <td className="ddv-td ddv-td-status">
                        {!slot.is_active ? (
                          <span className="badge bg-secondary">Inactive</span>
                        ) : activeAppts.length === 0 ? (
                          <span className="ddv-available">Available</span>
                        ) : activeAppts.length >= slot.max_patients ? (
                          <span className="badge bg-danger">Full</span>
                        ) : (
                          <span className="ddv-open">Open</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Legend */}
          <div className="ddv-legend">
            <span className="ddv-legend-item ddv-legend--confirmed">Confirmed</span>
            <span className="ddv-legend-item ddv-legend--pending">Pending</span>
            <span className="ddv-legend-item ddv-legend--empty">Available</span>
            <span className="ddv-legend-item ddv-legend--inactive">Inactive</span>
          </div>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {selectedAppt && (
        <div className="ddv-overlay" onClick={e => e.target === e.currentTarget && closeDetail()}>
          <div className="ddv-modal">
            <div className="ddv-modal-header">
              <div>
                <h5 className="ddv-modal-title">Appointment Details</h5>
                <div className="ddv-detail-ref">{formatRefId(selectedAppt._id)}</div>
              </div>
              <button className="ddv-modal-close" onClick={closeDetail} aria-label="Close">×</button>
            </div>

            <div className="ddv-modal-body">
              {modalSuccess && <div className="alert alert-success py-2 mb-3">{modalSuccess}</div>}

              {/* Status row */}
              <div className="ddv-detail-status-row">
                <span className={`${STATUS_BADGE[selectedAppt.status] || 'badge bg-secondary'} ddv-detail-badge`}>
                  {selectedAppt.status}
                </span>
                <select
                  className="form-select form-select-sm ddv-status-select"
                  value={selectedAppt.status}
                  disabled={updatingId === selectedAppt._id}
                  onChange={e => updateStatus(selectedAppt._id, e.target.value)}
                  aria-label="Change appointment status"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Appointment info */}
              <div className="ddv-detail-grid">
                <div className="ddv-detail-item">
                  <div className="ddv-detail-label">Time</div>
                  <div className="ddv-detail-value">{fmt12(selectedAppt.slot_time) || '—'}</div>
                </div>
                <div className="ddv-detail-item">
                  <div className="ddv-detail-label">Service</div>
                  <div className="ddv-detail-value">{selectedAppt.service}</div>
                </div>
                <div className="ddv-detail-item">
                  <div className="ddv-detail-label">Department</div>
                  <div className="ddv-detail-value">{selectedAppt.department}</div>
                </div>
                <div className="ddv-detail-item">
                  <div className="ddv-detail-label">Doctor</div>
                  <div className="ddv-detail-value">
                    {selectedAppt.slot_doctor ? `Dr. ${selectedAppt.slot_doctor}` : '—'}
                  </div>
                </div>
              </div>

              <div className="ddv-detail-divider" />

              {/* Patient info */}
              <div className="ddv-detail-grid">
                <div className="ddv-detail-item ddv-detail-item--full">
                  <div className="ddv-detail-label">Patient Name</div>
                  <div className="ddv-detail-value">{selectedAppt.patient_name}</div>
                </div>
                <div className="ddv-detail-item">
                  <div className="ddv-detail-label">Email</div>
                  <div className="ddv-detail-value ddv-detail-muted">{selectedAppt.patient_email}</div>
                </div>
                <div className="ddv-detail-item">
                  <div className="ddv-detail-label">Phone</div>
                  <div className="ddv-detail-value">{selectedAppt.patient_phone || '—'}</div>
                </div>
              </div>

              {selectedAppt.message && (
                <div className="ddv-detail-message">
                  <div className="ddv-detail-label">Patient Note</div>
                  <div className="ddv-detail-message-text">{selectedAppt.message}</div>
                </div>
              )}

              <div className="ddv-detail-divider" />

              {/* Admin notes */}
              <div className="mb-1">
                <label className="form-label ddv-detail-label" htmlFor="ddv-notes">
                  Admin Notes
                </label>
                <textarea
                  id="ddv-notes"
                  className="form-control ddv-notes-textarea"
                  rows={3}
                  placeholder="Add internal notes…"
                  value={detailNotes}
                  onChange={e => setDetailNotes(e.target.value)}
                />
                {notesError && <div className="ddv-notes-error">{notesError}</div>}
                {notesSaved && <div className="ddv-notes-saved">Notes saved.</div>}
              </div>
            </div>

            <div className="ddv-modal-footer">
              <button type="button" className="btn ddv-cancel-btn" onClick={closeDetail}>
                Close
              </button>
              <button
                type="button"
                className="btn ddv-save-btn"
                onClick={saveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? 'Saving…' : 'Save Notes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
